import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { commandCenterApi, dashboardApi } from '../services/api';
import { ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react';

/**
 * Closure Health tile — % of Joined applications that have a verified CTC
 * (either offered_ctc on the application OR a profile CTC on the candidate).
 *
 * Pairs with the MissingCtcBanner: the banner shows "fix me" when count > 0,
 * this tile turns the same data into a positive metric the team can drive
 * toward 100%.
 */
const ClosureHealthTile = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([
      commandCenterApi.getMissingCtc(1),
      dashboardApi.getStats(),
    ])
      .then(([missingRes, statsRes]) => {
        if (!alive) return;
        const missing = missingRes.data?.count || 0;
        const totalJoined = statsRes.data?.pipeline_stats?.joined || 0;
        setData({ totalJoined, missing });
      })
      .catch(() => {
        if (alive) setData({ totalJoined: 0, missing: 0 });
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4 flex items-center gap-3">
          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
          <span className="text-xs text-slate-500">Loading closure health…</span>
        </CardContent>
      </Card>
    );
  }
  if (!data || data.totalJoined === 0) {
    // No closures yet — hide the tile to avoid showing "100%" when there's nothing
    return null;
  }

  const verified = Math.max(0, data.totalJoined - data.missing);
  const pct = Math.round((verified / data.totalJoined) * 100);
  const tone = pct >= 95
    ? { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: 'text-emerald-600', label: 'Excellent' }
    : pct >= 80
    ? { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', icon: 'text-teal-600', label: 'Healthy' }
    : pct >= 60
    ? { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: 'text-amber-600', label: 'Needs attention' }
    : { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-800', icon: 'text-rose-600', label: 'At risk' };

  return (
    <Card
      className={`card-hover ${tone.bg} ${tone.border} border`}
      data-testid="closure-health-tile"
    >
      <CardContent className="p-4">
        <div className={`w-10 h-10 rounded-lg bg-white flex items-center justify-center mb-3 ${tone.icon}`}>
          {pct >= 80 ? <ShieldCheck className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
        </div>
        <div className="flex items-baseline gap-1">
          <p className={`text-2xl font-bold ${tone.text}`} data-testid="closure-health-pct">{pct}%</p>
          <p className={`text-xs font-mono ${tone.text} opacity-70`}>
            {verified}/{data.totalJoined}
          </p>
        </div>
        <p className="text-sm text-slate-600 leading-tight">Closure Health</p>
        <p className={`text-[11px] mt-0.5 font-medium ${tone.text}`}>
          {tone.label} · {data.missing} missing CTC
        </p>
      </CardContent>
    </Card>
  );
};

export default ClosureHealthTile;
